// from https://nextjs.org/docs/pages/building-your-application/configuring/error-handling

import { Component, ReactNode } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Close, ExpandCircleDown } from "@mui/icons-material";
import { TRPCClientErrorBase } from "@trpc/client";
import theme from "#next/theme";

interface Props {
  children?: ReactNode;
}

interface StateWithError {
  hasError: true;
  error: Error;
  expanded: boolean;
}

interface StateWithoutError {
  hasError: false;
}

type State = StateWithoutError | StateWithError;

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };
  public constructor(props: Props) {
    super(props);
  }

  public componentDidCatch(error: Error) {
    this.setState({
      hasError: true,
      error,
      expanded: false,
    });
  }

  private refresh() {
    document.location.reload();
  }

  private toggleDetails() {
    if (this.state.hasError) {
      const expanded = !this.state.expanded;
      this.setState({ ...this.state, expanded });
    }
  }

  render() {
    // Check if the error is thrown
    if (this.state.hasError) {
      return (
        <Fade appear={false} in={true}>
          <Paper
            role="dialog"
            aria-modal="false"
            aria-label="Cookie banner"
            square
            variant="outlined"
            tabIndex={-1}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              m: 0,
              p: 2,
              borderWidth: 0,
              borderTopWidth: 1,
              zIndex: 1201,
              backgroundColor: theme.palette.error.dark,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              gap={2}
            >
              <Box
                sx={{
                  flexShrink: 1,
                  alignSelf: { xs: "flex-start", sm: "center" },
                }}
              >
                <Typography fontWeight="bold" display="inline">
                  Error:{" "}
                </Typography>
                <Typography display="inline">
                  {this.state.error.message}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", gap: "0.5em", cursor: "pointer" }}
                  onClick={() => this.toggleDetails()}
                >
                  <ExpandCircleDown
                    fontSize={"small"}
                    sx={{
                      transform: this.state.expanded ? "scaleY(-1)" : undefined,
                    }}
                  />{" "}
                  {this.state.expanded ? "Hide Details" : "More Details"}
                </Typography>
                {this.state.expanded && (
                  <Typography
                    variant={"caption"}
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {this.state.error.stack}
                  </Typography>
                )}
              </Box>
              <Stack
                gap={2}
                direction={{
                  xs: "row-reverse",
                  sm: "row",
                }}
                sx={{
                  flexShrink: 0,
                  alignSelf: { xs: "flex-end", sm: "center" },
                }}
              >
                <Button size="small" onClick={() => this.refresh()}>
                  Refresh
                </Button>
                <Button
                  size={"small"}
                  onClick={() => report((this.state as StateWithError).error)}
                  variant="contained"
                >
                  Report
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Fade>
      );
    }

    // Return children components in case of no error

    return this.props.children;
  }
}

function isTRPCClientError(
  error: TRPCClientErrorBase<any> | Error,
): error is TRPCClientErrorBase<any> {
  return "data" in error && "shape" in error && "message" in error;
}

function report(error: TRPCClientErrorBase<any> | Error) {
  const message = isTRPCClientError(error) ? error.message : error.name;
  const lineBreak = "%0D%0A %0D%0A";
  const subject = `Bug report: ${message}`;
  const body = `Please add any additional details of what you were doing when the problem occurred.
${lineBreak}
---- Debugging info for Pyrra team follows ----
${lineBreak}
${message}
${lineBreak}
${isTRPCClientError(error) ? error.data.stack : error.stack}`;
  window.open(`mailto:help@pyrratech.com?subject=${subject}&body=${body}`);
}

export function ErrorModal({
  error,
  open,
  onClose,
}: {
  error: TRPCClientErrorBase<any> | Error;
  open: boolean;
  onClose(): void;
}) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        An unexpected error occurred
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Close />
      </IconButton>
      <DialogContent dividers>
        <Typography gutterBottom>{error.message}</Typography>
        {isTRPCClientError(error) ? (
          <TRPCErrorDetails error={error} />
        ) : (
          <JSErrorDetails error={error} />
        )}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={onClose}>
          OK
        </Button>
        <Button onClick={() => report(error)}>Report To Pyrra</Button>
      </DialogActions>
    </Dialog>
  );
}

function TRPCErrorDetails({ error }: { error: TRPCClientErrorBase<any> }) {
  return <Typography variant={"body2"}>{error.data.stack}</Typography>;
}

function JSErrorDetails({ error }: { error: Error }) {
  return (
    <Typography variant={"body2"} sx={{ whiteSpace: "pre-wrap" }}>
      {error.stack}
    </Typography>
  );
}
